import { ValidationValueMessage } from "react-hook-form";

type ValidateFn = (value: any) => Promise<boolean | string>;
type Validator = Record<string, ValidationValueMessage | ValidateFn>;

class FormValidation {
  private nameForField: string;
  private customValidations: ((
    value: any
  ) => (string | boolean) | Promise<string | boolean>)[];

  constructor(
    private currentFieldName: string,
    private currentFieldAliasName?: string,
    private currentValidator: Validator = {}
  ) {
    this.nameForField = this.currentFieldAliasName || this.currentFieldName;
    this.customValidations = [];
  }

  isRequired(message?: string) {
    this.currentValidator = {
      ...this.currentValidator,
      required: {
        value: true,
        message: message || `${this.nameForField} is required`,
      },
    };

    return this;
  }

  matchesField(
    watch: (fieldName: string) => Record<string, any>,
    otherFieldName: string,
    otherFieldAliasName?: string,
    message?: string
  ) {
    const nameForOtherField = otherFieldAliasName || otherFieldName;

    this.customValidations.push(
      (value: any) =>
        value === watch(otherFieldName) ||
        message ||
        `${this.nameForField} does not match ${nameForOtherField}`
    );

    return this;
  }

  addCustomValidator(validationFn: ValidateFn) {
    this.customValidations.push(validationFn);
    return this;
  }

  isValidName(message?: string) {
    this.currentValidator = {
      ...this.currentValidator,
      required: {
        value: true,
        message: "Name is Required",
      },
      minLength: {
        value: 3,
        message: "Enter a Valid Name",
      },
      pattern: {
        value: /^[a-zA-z]{1,}/,
        message: "Invalid Name",
      },
    };

    return this;
  }

  isValidEmail(message?: string) {
    this.currentValidator = {
      ...this.currentValidator,
      pattern: {
        value: /^([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/,
        message: message || "Invalid email address",
      },
    };

    return this;
  }

  isValidMobileNumber() {
    this.currentValidator = {
      ...this.currentValidator,
      pattern: {
        value: /^[0-9]{10,15}$/,
        message: "Invalid mobile number",
      },
    };

    return this;
  }
  // /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,}$/
  isValidPassword() {
    this.currentValidator = {
      ...this.currentValidator,
      // pattern: {
      //   value: /^[a-z0-9]+$/i,
      //   message: "Password must be alphanumeric",
      // },
      minLength: {
        value: 8,
        message: "Password length should not be below 8",
      },
      pattern: {
        value: /^(?=.*[A-Z])(?=.*[a-zA-Z0-9])(?=.*[\W_])[^\s]{8,}$/,
        message: "Password must be alphanumeric, contain at least one uppercase letter, one special character and must not have spaces",
      },
    };

    return this;
  }

  get Rules() {
    const validate = async (value: any) => {
      const validationResultPromises = await this.customValidations.map(
        (validator) => validator(value)
      );
      const validationResults = (
        await Promise.all(validationResultPromises)
      ).filter((result) => typeof result === "string");

      if (validationResults.length === 0) return true;
      else return validationResults[0];
    };

    return {
      ...this.currentValidator,
      validate,
    };
  }
}

export function validateField(
  fieldName: string,
  fieldAliasName?: string,
  validator: Validator = {}
) {
  return new FormValidation(fieldName, fieldAliasName, validator);
}
